import {
  SessionRole,
  useGetCommunity,
  useGetEvent,
  useGetParentCommunityIds,
  useGetSession,
  useGetSessionAttendees,
  useGetSessionLocation,
  useSortedSpeakersByFirstNameFromCache,
} from '@sunrise-ui/api-client';
import {
  Card,
  Colors,
  GET_CARD_CONTENT_GAP,
  SPEAKER_SEPARATOR_HEIGHT,
  TextBold,
  TextReg,
  isAndroid,
  isIos,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { SessionTimeLocationActions } from '../SessionTimeLocationActions';
import { CacheAwareSessionCardTags } from './CacheAwareSessionCardTags';

import { useSessionModal } from '../../../contexts/useSessionModal';
import { CommunityResult } from '../../communities/CommunityResult';
import { ProfileResult } from '../../profiles/ProfileResult';

type Props = {
  sessionId: string;
  style?: ViewStyle;
  isSingleSessionEvent?: boolean;
  pageCommunityId?: string;
};

/**
 * Queries expected:
 * Session
 * Event
 * Session Location
 * Session Attendees
 * Parent Community Ids
 * Community
 */
export function CacheAwareConfirmedSessionCard({
  sessionId,
  style,
  isSingleSessionEvent,
  pageCommunityId,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { openModal } = useSessionModal();

  const [, forceUpdate] = useState({});

  // Get individual session data
  const { data: session, isLoading: isLoadingSession } =
    useGetSession(sessionId);

  const { event_id: eventId } = session ?? {};
  const { data: event } = useGetEvent(eventId);
  const sessionLocationId = session?.location_id;
  const { data: sessionLocation, isLoading: isLoadingSessionLocation } =
    useGetSessionLocation(sessionLocationId);
  const { name: sessionLocationName } = sessionLocation ?? {};

  // Event + Parent communities for single-session parent display
  const communityId = event?.community_id;
  const {
    data: rawParentCommunityIds = [],
    isLoading: isLoadingParentCommunityIds,
  } = useGetParentCommunityIds({
    communityId,
  });

  const parentCommunityIds = rawParentCommunityIds?.filter(
    (id) => id !== pageCommunityId,
  );

  const { data: eventCommunity } = useGetCommunity(communityId);

  // Get session attendees
  const { data: attendees, isLoading: isLoadingAttendees } =
    useGetSessionAttendees(sessionId, false);

  // Filter speakers from attendees
  const speakers = attendees?.filter(
    (a) => a.role === SessionRole.SPEAKER || a.role === SessionRole.MODERATOR,
  );

  // Sort speakers alphabetically by first name using custom hook
  const { sortedSpeakers } = useSortedSpeakersByFirstNameFromCache(speakers);

  // Simple loading state that covers all data requirements
  const isLoading = isLoadingSession || isLoadingAttendees;

  // Check if session is currently ongoing
  const isSessionOngoing = useMemo(() => {
    if (!session?.start_date || !session?.end_date) return false;

    const now = DateTime.now();
    const startTime = DateTime.fromISO(session.start_date);
    const endTime = DateTime.fromISO(session.end_date);

    return now >= startTime && now <= endTime;
  }, [session]);

  // Check if session starts within 24 hours (for purple shadow)
  const isStartingWithin24Hours = useMemo(() => {
    if (!session?.start_date) return false;

    const now = DateTime.now();
    const startTime = DateTime.fromISO(session.start_date);

    // Only show purple if session hasn't started yet and starts within 24 hours
    if (startTime <= now) return false;

    const hoursUntilStart = startTime.diff(now, 'hours').hours;
    return hoursUntilStart <= 24;
  }, [session?.start_date]);

  // Animation progress for border pulse
  const animationProgress = useSharedValue(0);

  // Determine if we should animate (ongoing or starting within 24 hours)
  const shouldAnimate = isSessionOngoing || isStartingWithin24Hours;

  // Start/stop animation based on session status
  useEffect(() => {
    if (shouldAnimate) {
      animationProgress.value = withRepeat(
        withTiming(1, { duration: 1500 }),
        -1,
        true,
      );
    } else {
      animationProgress.value = withTiming(0, { duration: 300 });
    }
  }, [shouldAnimate, animationProgress]);

  // Animated border style
  const animatedBorderStyle = useAnimatedStyle(() => {
    if (!shouldAnimate)
      return {
        borderColor: 'transparent',
        borderWidth: 1.5,
      };

    const borderColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      isSessionOngoing
        ? [Colors.pink0, 'transparent']
        : isStartingWithin24Hours
          ? [Colors.purple1, 'transparent']
          : ['transparent', 'transparent'],
    );

    return {
      borderColor,
      borderWidth: 1.5,
    };
  });

  // Set up timeouts to update the component when session status changes
  useEffect(() => {
    if (!session?.start_date || !session?.end_date) return;

    let timeout: NodeJS.Timeout;
    const now = DateTime.now();
    const startTime = DateTime.fromISO(session.start_date);
    const endTime = DateTime.fromISO(session.end_date);

    // If session hasn't started yet, set timeout for when it starts
    if (now < startTime) {
      const msUntilStart = startTime.diff(now).milliseconds;
      if (msUntilStart > 0 && msUntilStart < 24 * 60 * 60 * 1000) {
        // Only if within 24 hours
        timeout = setTimeout(() => {
          forceUpdate({});
        }, msUntilStart);
      }
    } else if (now <= endTime) {
      const msUntilEnd = endTime.diff(now).milliseconds;
      if (msUntilEnd > 0 && msUntilEnd < 24 * 60 * 60 * 1000) {
        // Only if within 24 hours
        timeout = setTimeout(() => {
          forceUpdate({});
        }, msUntilEnd);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [session?.start_date, session?.end_date]);

  return (
    <Animated.View style={[styles.animatedWrapper, style, animatedBorderStyle]}>
      <Card
        style={[styles.sessionCard]}
        shadowColor={
          isSessionOngoing
            ? Colors.pink0
            : isStartingWithin24Hours
              ? Colors.purple1
              : isAndroid
                ? Colors.opacity40
                : Colors.opacity20
        }
        shadowOpacity={1}
        onPress={() => {
          if (isSingleSessionEvent && pageCommunityId) {
            const slug = eventCommunity?.slug;
            if (pageCommunityId === communityId || !slug) {
              // Open session details modal when on event's community page or no slug
              openModal(sessionId);
            } else {
              // Navigate to event's community page when not on it
              router.navigate(`/communities/${slug}`);
            }
          } else {
            // Always open modal for multi-session events or when event community is unknown
            openModal(sessionId);
          }
        }}
      >
        <View style={styles.cardContent}>
          <SessionTimeLocationActions
            startDate={session?.start_date}
            endDate={session?.end_date}
            locationName={sessionLocationName}
            isLoadingLocation={isLoadingSessionLocation}
          />
          <CacheAwareSessionCardTags sessionId={sessionId} eventId={eventId} />
          {isLoadingAttendees ? (
            <View style={styles.skeletonContainer}>
              {Array(2)
                .fill(0)
                .map((_, index) => (
                  <ProfileResult
                    key={`skeleton-${index}`}
                    style={styles.profileResult}
                    size="small"
                  />
                ))}
            </View>
          ) : (sortedSpeakers?.length ?? 0) > 0 ? (
            <View style={styles.speakerList}>
              {sortedSpeakers?.map((speaker, index) => (
                <ProfileResult
                  key={index}
                  userId={speaker.user_id}
                  style={styles.profileResult}
                  size="small"
                  isOneLineText={true}
                />
              ))}
            </View>
          ) : null}

          {isSingleSessionEvent && isLoadingParentCommunityIds ? (
            <View style={styles.skeletonContainer}>
              {Array(2)
                .fill(0)
                .map((_, index) => (
                  <ProfileResult
                    key={`parents-skeleton-${index}`}
                    style={styles.profileResult}
                    size="small"
                  />
                ))}
            </View>
          ) : isSingleSessionEvent && (parentCommunityIds?.length ?? 0) > 0 ? (
            <View style={styles.speakerList}>
              {parentCommunityIds?.map((item, index) => (
                <CommunityResult
                  key={index}
                  communityId={item}
                  size="small"
                  shouldNavigateOnClick={true}
                  isOneLineText={true}
                  style={styles.profileResult}
                />
              ))}
            </View>
          ) : null}

          {isLoading ? (
            <View style={styles.loadingSkeleton}>
              <View style={styles.titleSkeleton} />
              <View style={styles.descriptionSkeleton} />
              <View style={styles.descriptionSkeleton} />
              <View style={styles.descriptionSkeleton} />
              <View style={styles.descriptionSkeleton} />
            </View>
          ) : (
            <View style={styles.titleAndDescriptionContainer}>
              <TextBold style={styles.title}>{session?.topic_name}</TextBold>
              <TextReg style={styles.description} numberOfLines={4}>
                {session?.topic_description}
              </TextReg>
            </View>
          )}
        </View>
      </Card>
    </Animated.View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    touchableWrapper: {
      flex: 1,
      width: '100%',
    },
    cardContent: {
      width: '100%',
      flex: 1,
      overflow: 'hidden',
      padding: isMobile ? 12 : 24,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      position: 'relative',
      borderRadius: 12,
      gap: GET_CARD_CONTENT_GAP(isMobile),
    },
    sessionCard: {
      minWidth: isMobile ? 0 : 200,
      padding: 0,
      borderRadius: 12,
    },
    editButton: {
      minWidth: 32,
      minHeight: 32,
    },
    speakerSection: {
      gap: isMobile ? 8 : 12,
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
      justifyContent: 'space-between',
    },
    profileResult: {
      width: '100%',
    },
    titleAndDescriptionContainer: {
      width: '100%',
      gap: 12,
    },
    titleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    title: {
      fontSize: isMobile ? 20 : 24,
      marginRight: 8,
    },
    startTime: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8,
      flexShrink: 0,
    },
    description: {
      fontSize: isMobile ? 14 : 16,
      paddingRight: isMobile ? 24 : 26,
    },
    speakerList: {
      width: '100%',
      gap: SPEAKER_SEPARATOR_HEIGHT,
    },
    skeletonContainer: {
      gap: 12,
    },
    listSeparator: {
      height: SPEAKER_SEPARATOR_HEIGHT,
    },
    listColumn: {
      gap: 4,
    },
    loadingSkeleton: {
      width: '100%',
    },
    titleSkeleton: {
      maxWidth: 200,
      height: isMobile ? 20 : 24,
      marginTop: isAndroid ? 3.5 : isIos ? 4.15 : isMobile ? 5 : 5.5,
      marginBottom:
        (isAndroid ? 3.5 : isIos ? 4.15 : isMobile ? 5 : 5.5) +
        (isMobile ? 8 : 4),
      borderRadius: 16,
      backgroundColor: Colors.opacity20,
    },
    descriptionSkeleton: {
      height: isMobile ? 14 : 16,
      marginVertical: isAndroid ? 0.3375 : isIos ? 2.875 : isMobile ? 3.5 : 4.5,
      borderRadius: 16,
      backgroundColor: Colors.opacity20,
      marginRight: 28,
    },
    animatedWrapper: {
      overflow: 'visible',
      borderRadius: 12,
    },
  });
